/**
 * useOFHelpers - Custom hook com funções utilitárias para OFs
 * Centraliza badges, cores e configurações
 */
import { FileText, CheckCircle, Clock, Play, Pause, Check, XCircle } from 'lucide-react';

// Configuração de Estados
export const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho', color: 'bg-stone-100 text-stone-600', icon: FileText },
  pendente: { label: 'Ready to Start', color: 'bg-blue-100 text-blue-600', icon: CheckCircle },
  aguardar_entrada: { label: 'Aguardar Entrada', color: 'bg-amber-100 text-amber-600', icon: Clock },
  em_curso: { label: 'Em Curso', color: 'bg-green-100 text-green-600', icon: Play },
  pausado: { label: 'Pausado', color: 'bg-orange-100 text-orange-600', icon: Pause },
  terminado: { label: 'Terminado', color: 'bg-emerald-100 text-emerald-600', icon: Check },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-600', icon: XCircle },
};

// Transições válidas de estado
export const TRANSICOES_VALIDAS = {
  rascunho: ['pendente', 'aguardar_entrada', 'em_curso', 'cancelado'],
  pendente: ['em_curso', 'aguardar_entrada', 'cancelado'],
  aguardar_entrada: ['em_curso', 'pendente', 'cancelado'],
  em_curso: ['pausado', 'terminado', 'cancelado'],
  pausado: ['em_curso', 'terminado', 'cancelado'],
  terminado: [],
  cancelado: ['rascunho'],
};

// Cores de grupos de operações
export const CORES_GRUPOS = {
  A: 'border-blue-400',
  B: 'border-green-400',
  C: 'border-purple-400',
  D: 'border-orange-400',
  E: 'border-pink-400',
  F: 'border-cyan-400',
};

/**
 * Hook que retorna funções utilitárias para OFs
 */
export default function useOFHelpers() {
  /**
   * Retorna o badge JSX para um status
   */
  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.rascunho;
    const Icon = config.icon;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 ${config.color}`}>
        <Icon className="h-3.5 w-3.5" />
        {config.label}
      </span>
    );
  };

  /**
   * Retorna a classe CSS para a cor de um grupo
   */
  const getGrupoCor = (grupo) => {
    if (!grupo) return 'border-stone-200';
    return CORES_GRUPOS[grupo] || 'border-stone-200';
  };

  /**
   * Formata tempo em segundos para string legível
   */
  const formatTempo = (segundos) => {
    if (!segundos) return '0h 0m';
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    return `${horas}h ${minutos}m`;
  };

  /**
   * Calcula percentagem de progresso
   */
  const calcularProgresso = (atual, total) => {
    if (!total || total === 0) return 0;
    return Math.round((atual / total) * 100);
  };

  /**
   * Retorna classe CSS para status de operação
   */
  const getOperacaoStatusClass = (status) => {
    switch (status) {
      case 'concluida':
        return 'bg-green-100 text-green-600';
      case 'em_andamento':
        return 'bg-amber-100 text-amber-600';
      case 'em_progresso':
        return 'bg-blue-100 text-blue-600';
      default:
        return 'bg-stone-100 text-stone-600';
    }
  };

  /**
   * Retorna label para status de operação
   */
  const getOperacaoStatusLabel = (status) => {
    switch (status) {
      case 'concluida':
        return 'Concluída';
      case 'em_andamento':
        return 'Em Andamento';
      case 'em_progresso':
        return 'Em Progresso';
      default:
        return 'Pendente';
    }
  };

  return {
    getStatusBadge,
    getGrupoCor,
    formatTempo,
    calcularProgresso,
    getOperacaoStatusClass,
    getOperacaoStatusLabel,
    STATUS_CONFIG,
    TRANSICOES_VALIDAS,
    CORES_GRUPOS,
  };
}
